import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { MessageCircle, Send, ArrowLeft, User, Lock, Search, RefreshCw, Plus, X, Camera, Loader2, Film, Play, Trash2, AlertTriangle, Shield, Check, UserPlus, Inbox } from 'lucide-react';
import { nip19 } from 'nostr-tools';
import { useAppContext } from '../hooks/useAppContext';
import { useNostrQuery, useNostrProfile } from '../hooks/useNostrQuery';
import { useDisplayName } from '../hooks/useNip05Verified';
import VerifiedBadge from '../components/VerifiedBadge';
import { useNostrPublish, getReadRelays } from '../hooks/useNostrPublish';
import { useFollows } from '../hooks/useFollows';
import { timeAgo } from '../lib/utils';
import { uploadMedia, UploadSizeError, type CompressionProgress } from '../lib/imageUpload';

// ── Per-conversation last-read tracking ───────────────────────────
// Tracks the timestamp of the last message the user has seen in each
// conversation, so we can show unread indicators per-conversation.
const DM_CONVO_READ_KEY = 'bchnostr:dm-convo-read';

function getConvoReadTimes(): Record<string, number> {
  try {
    const raw = localStorage.getItem(DM_CONVO_READ_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
}

function markConvoRead(pubkey: string) {
  try {
    const times = getConvoReadTimes();
    times[pubkey] = Math.floor(Date.now() / 1000);
    localStorage.setItem(DM_CONVO_READ_KEY, JSON.stringify(times));
  } catch {}
}

function getConvoReadTime(pubkey: string): number {
  return getConvoReadTimes()[pubkey] || 0;
}

// ── Local deletion tracking ──────────────────────────────────────
// Persists deleted message IDs in localStorage so they stay hidden
// across sessions, even if relays don't honor the NIP-09 delete.
const DELETED_DMS_KEY = 'bchnostr:deleted-dms';

function getDeletedDMs(): Set<string> {
  try {
    const raw = localStorage.getItem(DELETED_DMS_KEY);
    return raw ? new Set(JSON.parse(raw)) : new Set();
  } catch { return new Set(); }
}

function markDMDeleted(eventId: string) {
  try {
    const set = getDeletedDMs();
    set.add(eventId);
    localStorage.setItem(DELETED_DMS_KEY, JSON.stringify([...set]));
  } catch {}
}

// ── Accepted DM requests tracking ────────────────────────────────
// When a user accepts a message request from someone they don't
// follow, we persist that decision so it stays accepted.
const ACCEPTED_REQUESTS_KEY = 'bchnostr:accepted-dm-requests';

function getAcceptedRequests(): Set<string> {
  try {
    const raw = localStorage.getItem(ACCEPTED_REQUESTS_KEY);
    return raw ? new Set(JSON.parse(raw)) : new Set();
  } catch { return new Set(); }
}

function acceptRequest(pubkey: string) {
  try {
    const set = getAcceptedRequests();
    set.add(pubkey);
    localStorage.setItem(ACCEPTED_REQUESTS_KEY, JSON.stringify([...set]));
  } catch {}
}

function declineRequest(pubkey: string) {
  // We just track declined in the same way — using a separate key
  // so the user doesn't see the request again
  try {
    const set = getAcceptedRequests();
    // We use a "declined:" prefix to differentiate
    set.add('declined:' + pubkey);
    localStorage.setItem(ACCEPTED_REQUESTS_KEY, JSON.stringify([...set]));
  } catch {}
}

function isRequestAccepted(pubkey: string): boolean {
  return getAcceptedRequests().has(pubkey);
}

function isRequestDeclined(pubkey: string): boolean {
  return getAcceptedRequests().has('declined:' + pubkey);
}

type DMTab = 'primary' | 'requests';

export default function MessagesPage() {
  const { user } = useAppContext();
  const [searchParams] = useSearchParams();
  const [selectedPubkey, setSelectedPubkey] = useState(searchParams.get('to') || '');
  const [conversations, setConversations] = useState<string[]>([]);
  const [showNewDM, setShowNewDM] = useState(false);
  const [newDMInput, setNewDMInput] = useState('');
  const [newDMError, setNewDMError] = useState('');
  const [dmTab, setDmTab] = useState<DMTab>('primary');
  const [acceptedSet, setAcceptedSet] = useState<Set<string>>(() => getAcceptedRequests());
  const { follows } = useFollows(user?.pubkey);
  const followSet = useMemo(() => new Set(follows), [follows]);
  const [convoReadTimes, setConvoReadTimes] = useState<Record<string, number>>(() => getConvoReadTimes());

  // Fetch DMs (kind 4) for current user — use all relays and longer window
  const [since] = useState(() => Math.floor(Date.now() / 1000) - 86400 * 90);
  const { events: dmEvents, refetch } = useNostrQuery({
    filters: user ? [
      { kinds: [4], authors: [user.pubkey], limit: 500, since },
      { kinds: [4], '#p': [user.pubkey], limit: 500, since },
    ] : [],
    enabled: !!user,
    allRelays: true,
  });

  const handleStartDM = () => {
    setNewDMError('');
    const input = newDMInput.trim();
    if (!input) return;
    try {
      let pubkey = input;
      if (input.startsWith('npub1')) {
        const decoded = nip19.decode(input);
        if (decoded.type !== 'npub') throw new Error('Invalid npub');
        pubkey = decoded.data as string;
      } else if (input.startsWith('nprofile1')) {
        const decoded = nip19.decode(input);
        if (decoded.type !== 'nprofile') throw new Error('Invalid nprofile');
        pubkey = decoded.data.pubkey;
      }
      // Validate hex pubkey
      if (!/^[0-9a-f]{64}$/i.test(pubkey)) throw new Error('Invalid pubkey format');
      if (pubkey === user?.pubkey) throw new Error("You can't message yourself");
      setSelectedPubkey(pubkey);
      setShowNewDM(false);
      setNewDMInput('');
      handleMarkConvoRead(pubkey);
      // Add to conversations if not already there
      setConversations(prev => prev.includes(pubkey) ? prev : [pubkey, ...prev]);
    } catch (e) {
      setNewDMError(e instanceof Error ? e.message : 'Invalid input');
    }
  };

  // Build conversation list
  useEffect(() => {
    if (!user) return;
    const peers = new Set<string>();
    dmEvents.forEach(ev => {
      if (ev.pubkey === user.pubkey) {
        const recipient = ev.tags.find(t => t[0] === 'p')?.[1];
        if (recipient) peers.add(recipient);
      } else {
        peers.add(ev.pubkey);
      }
    });
    setConversations([...peers]);
  }, [dmEvents, user]);

  // Split conversations into primary (followed / accepted / I messaged them first)
  // and requests (strangers who messaged me, not yet accepted)
  const { primaryConvos, requestConvos } = useMemo(() => {
    if (!user) return { primaryConvos: [], requestConvos: [] };
    const primary: string[] = [];
    const requests: string[] = [];

    for (const pubkey of conversations) {
      // Always primary: people I follow, or requests I've accepted
      if (followSet.has(pubkey) || acceptedSet.has(pubkey)) {
        primary.push(pubkey);
        continue;
      }

      // Check if I initiated the conversation (I sent the first message)
      const myMessagesToThem = dmEvents.filter(
        ev => ev.pubkey === user.pubkey && ev.tags.some((t: string[]) => t[0] === 'p' && t[1] === pubkey)
      );
      const theirMessagesToMe = dmEvents.filter(
        ev => ev.pubkey === pubkey && ev.tags.some((t: string[]) => t[0] === 'p' && t[1] === user.pubkey)
      );

      if (myMessagesToThem.length > 0 && (theirMessagesToMe.length === 0 ||
        myMessagesToThem[myMessagesToThem.length - 1]?.created_at <= (theirMessagesToMe[0]?.created_at ?? Infinity))) {
        // I sent the first message — treat as primary
        primary.push(pubkey);
        continue;
      }

      // Skip declined requests
      if (acceptedSet.has('declined:' + pubkey)) continue;

      // This person messaged me but I don't follow them — request
      requests.push(pubkey);
    }

    return { primaryConvos: primary, requestConvos: requests };
  }, [conversations, followSet, acceptedSet, dmEvents, user]);

  // ── Per-conversation latest message & unread tracking ──────────
  // Build a map of peer pubkey -> { latestTimestamp, latestFromOther, unreadCount }
  const convoMeta = useMemo(() => {
    if (!user) return new Map<string, { latestTimestamp: number; unreadCount: number }>();
    const map = new Map<string, { latestTimestamp: number; unreadCount: number }>();

    for (const pubkey of conversations) {
      const readTime = convoReadTimes[pubkey] || 0;
      let latestTimestamp = 0;
      let unreadCount = 0;

      dmEvents.forEach(ev => {
        const isFromMe = ev.pubkey === user.pubkey && ev.tags.some((t: string[]) => t[0] === 'p' && t[1] === pubkey);
        const isToMe = ev.pubkey === pubkey && ev.tags.some((t: string[]) => t[0] === 'p' && t[1] === user.pubkey);
        if (!isFromMe && !isToMe) return;

        // Track the latest message in this convo (from either side)
        if (ev.created_at > latestTimestamp) latestTimestamp = ev.created_at;

        // Count unread: only messages FROM the other person, after last read time
        if (isToMe && ev.created_at > readTime) {
          unreadCount++;
        }
      });

      map.set(pubkey, { latestTimestamp, unreadCount });
    }
    return map;
  }, [conversations, dmEvents, user, convoReadTimes]);

  // Sort primary conversations: unread first (by latest time), then read by latest time
  const sortedPrimaryConvos = useMemo(() => {
    return [...primaryConvos].sort((a, b) => {
      const metaA = convoMeta.get(a);
      const metaB = convoMeta.get(b);
      const unreadA = metaA?.unreadCount || 0;
      const unreadB = metaB?.unreadCount || 0;
      // Unread conversations first
      if (unreadA > 0 && unreadB === 0) return -1;
      if (unreadA === 0 && unreadB > 0) return 1;
      // Then by latest message time (most recent first)
      return (metaB?.latestTimestamp || 0) - (metaA?.latestTimestamp || 0);
    });
  }, [primaryConvos, convoMeta]);

  // Sort request conversations by latest time
  const sortedRequestConvos = useMemo(() => {
    return [...requestConvos].sort((a, b) => {
      const metaA = convoMeta.get(a);
      const metaB = convoMeta.get(b);
      return (metaB?.latestTimestamp || 0) - (metaA?.latestTimestamp || 0);
    });
  }, [requestConvos, convoMeta]);

  // Total unread count for primary conversations
  const totalUnread = useMemo(() => {
    let count = 0;
    for (const pubkey of primaryConvos) {
      count += convoMeta.get(pubkey)?.unreadCount || 0;
    }
    return count;
  }, [primaryConvos, convoMeta]);

  // Mark a conversation as read
  const handleMarkConvoRead = useCallback((pubkey: string) => {
    markConvoRead(pubkey);
    setConvoReadTimes(prev => ({ ...prev, [pubkey]: Math.floor(Date.now() / 1000) }));
  }, []);

  const handleAcceptRequest = (pubkey: string) => {
    acceptRequest(pubkey);
    setAcceptedSet(prev => {
      const next = new Set(prev);
      next.add(pubkey);
      return next;
    });
  };

  const handleDeclineRequest = (pubkey: string) => {
    declineRequest(pubkey);
    setAcceptedSet(prev => {
      const next = new Set(prev);
      next.add('declined:' + pubkey);
      return next;
    });
    // If this was the selected conversation, deselect
    if (selectedPubkey === pubkey) setSelectedPubkey('');
  };

  const activeConvos = dmTab === 'primary' ? sortedPrimaryConvos : sortedRequestConvos;

  // Mark conversation as read when selecting it
  const handleSelectConvo = useCallback((pubkey: string) => {
    setSelectedPubkey(pubkey);
    handleMarkConvoRead(pubkey);
  }, [handleMarkConvoRead]);

  // Mark the deep-linked conversation as read when it loads
  useEffect(() => {
    if (selectedPubkey) {
      handleMarkConvoRead(selectedPubkey);
    }
  }, [selectedPubkey, handleMarkConvoRead]);

  if (!user) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4 p-4">
        <Lock className="w-16 h-16 text-muted-foreground/30" />
        <h2 className="text-lg font-semibold">Log in to access messages</h2>
        <p className="text-sm text-muted-foreground text-center">
          Your direct messages are encrypted end-to-end using NIP-04.
        </p>
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100vh-64px)] lg:h-screen">
      {/* Conversation List */}
      <div className={`w-full md:w-80 border-r border-border flex flex-col ${selectedPubkey ? 'hidden md:flex' : 'flex'}`}>
        <div className="p-4 border-b border-border">
          <div className="flex items-center justify-between mb-1">
            <h1 className="text-lg font-bold flex items-center gap-2">
              <MessageCircle className="w-5 h-5 text-primary" /> Messages
            </h1>
            <div className="flex items-center gap-1">
              <button
                onClick={refetch}
                className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                title="Refresh messages"
              >
                <RefreshCw className="w-4 h-4" />
              </button>
              <button
                onClick={() => { setShowNewDM(v => !v); setNewDMError(''); setNewDMInput(''); }}
                className={`p-1.5 rounded-lg transition-colors ${showNewDM ? 'bg-primary/10 text-primary' : 'hover:bg-muted text-muted-foreground hover:text-foreground'}`}
                title="New message"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
          </div>
          <p className="text-xs text-muted-foreground flex items-center gap-1">
            <Lock className="w-3 h-3" /> End-to-end encrypted
          </p>

          {/* Primary / Requests tabs */}
          <div className="flex gap-1 mt-2.5 bg-muted/60 rounded-lg p-0.5">
            <button
              onClick={() => setDmTab('primary')}
              className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-md text-xs font-medium transition-colors ${
                dmTab === 'primary'
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <MessageCircle className="w-3 h-3" /> Primary
              {totalUnread > 0 && (
                <span className="text-[9px] font-bold bg-primary text-primary-foreground px-1.5 py-0.5 rounded-full tabular-nums">
                  {totalUnread > 9 ? '9+' : totalUnread}
                </span>
              )}
            </button>
            <button
              onClick={() => setDmTab('requests')}
              className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-md text-xs font-medium transition-colors ${
                dmTab === 'requests'
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Inbox className="w-3 h-3" /> Requests
              {requestConvos.length > 0 && (
                <span className="text-[9px] font-bold bg-amber-500 text-white px-1.5 py-0.5 rounded-full tabular-nums">
                  {requestConvos.length}
                </span>
              )}
            </button>
          </div>

          {/* New DM input */}
          {showNewDM && (
            <div className="mt-3 space-y-2">
              <p className="text-xs font-medium">Start a new conversation</p>
              <div className="flex gap-2">
                <input
                  autoFocus
                  value={newDMInput}
                  onChange={e => { setNewDMInput(e.target.value); setNewDMError(''); }}
                  onKeyDown={e => { if (e.key === 'Enter') handleStartDM(); }}
                  placeholder="npub1... or hex pubkey"
                  className="flex-1 bg-muted rounded-lg px-3 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-primary placeholder:text-muted-foreground"
                />
                <button
                  onClick={handleStartDM}
                  disabled={!newDMInput.trim()}
                  className="px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-medium disabled:opacity-50 hover:bg-primary/90"
                >
                  Go
                </button>
              </div>
              {newDMError && <p className="text-xs text-red-500">{newDMError}</p>}
            </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto">
          {/* Requests tab info banner */}
          {dmTab === 'requests' && requestConvos.length > 0 && (
            <div className="flex items-start gap-2.5 px-4 py-3 border-b border-border bg-amber-500/5">
              <Shield className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
              <p className="text-[11px] text-muted-foreground leading-relaxed">
                Messages from people you don't follow. Content is hidden until you accept the request.
              </p>
            </div>
          )}

          {activeConvos.length === 0 ? (
            <div className="text-center py-12 px-4">
              {dmTab === 'primary' ? (
                <>
                  <MessageCircle className="w-10 h-10 mx-auto text-muted-foreground/30 mb-2" />
                  <p className="text-sm text-muted-foreground">No conversations yet.</p>
                  <p className="text-xs text-muted-foreground mt-1">Tap <strong>+</strong> above to start a new conversation.</p>
                </>
              ) : (
                <>
                  <Shield className="w-10 h-10 mx-auto text-muted-foreground/30 mb-2" />
                  <p className="text-sm text-muted-foreground">No message requests.</p>
                  <p className="text-xs text-muted-foreground mt-1">Messages from strangers will appear here for your review.</p>
                </>
              )}
            </div>
          ) : (
            activeConvos.map(pubkey => (
              dmTab === 'requests' ? (
                <RequestItem
                  key={pubkey}
                  pubkey={pubkey}
                  isActive={selectedPubkey === pubkey}
                  onAccept={() => handleAcceptRequest(pubkey)}
                  onDecline={() => handleDeclineRequest(pubkey)}
                  onClick={() => handleSelectConvo(pubkey)}
                />
              ) : (
                <ConversationItem
                  key={pubkey}
                  pubkey={pubkey}
                  isActive={selectedPubkey === pubkey}
                  onClick={() => handleSelectConvo(pubkey)}
                  unreadCount={convoMeta.get(pubkey)?.unreadCount || 0}
                  latestTimestamp={convoMeta.get(pubkey)?.latestTimestamp || 0}
                />
              )
            ))
          )}
        </div>
      </div>

      {/* Chat Area */}
      <div className={`flex-1 flex flex-col ${selectedPubkey ? 'flex' : 'hidden md:flex'}`}>
        {selectedPubkey ? (
          // Check if this is an unaccepted request — show gated view
          !followSet.has(selectedPubkey) && !acceptedSet.has(selectedPubkey) && requestConvos.includes(selectedPubkey) ? (
            <PendingRequestView
              peerPubkey={selectedPubkey}
              onBack={() => setSelectedPubkey('')}
              onAccept={() => handleAcceptRequest(selectedPubkey)}
              onDecline={() => handleDeclineRequest(selectedPubkey)}
            />
          ) : (
            <ChatArea
              peerPubkey={selectedPubkey}
              myPubkey={user.pubkey}
              onBack={() => setSelectedPubkey('')}
              dmEvents={dmEvents}
            />
          )
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <MessageCircle className="w-16 h-16 mx-auto text-muted-foreground/20 mb-3" />
              <p className="text-muted-foreground">Select a conversation</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ConversationItem({ pubkey, isActive, onClick, unreadCount, latestTimestamp }: {
  pubkey: string; isActive: boolean; onClick: () => void; unreadCount: number; latestTimestamp: number;
}) {
  const { profile } = useNostrProfile(pubkey);
  const name = useDisplayName(pubkey, profile, pubkey.slice(0, 12));
  const hasUnread = unreadCount > 0;
  
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center gap-3 px-4 py-3 hover:bg-muted/50 transition-colors text-left ${
        isActive ? 'bg-primary/5 border-r-2 border-primary' : ''
      } ${hasUnread && !isActive ? 'bg-primary/[0.03]' : ''}`}
    >
      {/* Avatar with unread dot */}
      <div className="relative shrink-0">
        {profile?.picture ? (
          <img src={profile.picture} alt="" className="w-10 h-10 rounded-full object-cover" />
        ) : (
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
            <User className="w-5 h-5 text-primary/60" />
          </div>
        )}
        {hasUnread && (
          <div className="absolute -top-0.5 -right-0.5 w-4 h-4 rounded-full bg-primary border-2 border-background flex items-center justify-center">
            <span className="text-[8px] font-bold text-primary-foreground">{unreadCount > 9 ? '9+' : unreadCount}</span>
          </div>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1 min-w-0">
            <p className={`text-sm truncate ${hasUnread ? 'font-bold text-foreground' : 'font-medium'}`}>
              {name}
            </p>
            <VerifiedBadge nip05={profile?.nip05} pubkey={pubkey} size="xs" />
          </div>
          {latestTimestamp > 0 && (
            <span className={`text-[10px] shrink-0 ${hasUnread ? 'text-primary font-semibold' : 'text-muted-foreground'}`}>
              {timeAgo(latestTimestamp)}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          <p className={`text-xs truncate ${hasUnread ? 'text-foreground/70 font-medium' : 'text-muted-foreground'}`}>
            {hasUnread ? `${unreadCount} new message${unreadCount !== 1 ? 's' : ''}` : 'Encrypted conversation'}
          </p>
          {hasUnread && (
            <div className="w-2 h-2 rounded-full bg-primary shrink-0" />
          )}
        </div>
      </div>
    </button>
  );
}

function RequestItem({ pubkey, isActive, onAccept, onDecline, onClick }: {
  pubkey: string; isActive: boolean; onAccept: () => void; onDecline: () => void; onClick: () => void;
}) {
  const { profile } = useNostrProfile(pubkey);
  const name = useDisplayName(pubkey, profile, pubkey.slice(0, 12));

  return (
    <div className={`flex items-center gap-3 px-4 py-3 border-b border-border transition-colors ${
      isActive ? 'bg-primary/5 border-r-2 border-r-primary' : 'hover:bg-muted/30'
    }`}>
      <button onClick={onClick} className="flex items-center gap-3 flex-1 min-w-0 text-left">
        {profile?.picture ? (
          <img src={profile.picture} alt="" className="w-10 h-10 rounded-full object-cover opacity-70" />
        ) : (
          <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
            <User className="w-5 h-5 text-muted-foreground/40" />
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1 min-w-0">
            <p className="font-medium text-sm truncate">{name}</p>
            <VerifiedBadge nip05={profile?.nip05} pubkey={pubkey} size="xs" />
          </div>
          <p className="text-[10px] text-muted-foreground flex items-center gap-1">
            <Lock className="w-2.5 h-2.5" /> Message hidden — tap to review
          </p>
        </div>
      </button>
      <div className="flex items-center gap-1 shrink-0">
        <button
          onClick={onAccept}
          className="p-1.5 rounded-lg bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
          title="Accept"
        >
          <Check className="w-3.5 h-3.5" />
        </button>
        <button
          onClick={onDecline}
          className="p-1.5 rounded-lg bg-red-500/10 text-red-500 hover:bg-red-500/20 transition-colors"
          title="Decline"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

function PendingRequestView({ peerPubkey, onBack, onAccept, onDecline }: {
  peerPubkey: string; onBack: () => void; onAccept: () => void; onDecline: () => void;
}) {
  const { profile } = useNostrProfile(peerPubkey);
  const name = useDisplayName(peerPubkey, profile, peerPubkey.slice(0, 12));

  return (
    <>
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-border">
        <button onClick={onBack} className="md:hidden p-1 rounded-lg hover:bg-muted">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <Link to={`/profile/${peerPubkey}`} className="flex items-center gap-2">
          {profile?.picture ? (
            <img src={profile.picture} alt="" className="w-8 h-8 rounded-full object-cover opacity-70" />
          ) : (
            <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
              <User className="w-4 h-4 text-muted-foreground/40" />
            </div>
          )}
          <div>
            <div className="flex items-center gap-1">
              <p className="font-semibold text-sm">{name}</p>
              <VerifiedBadge nip05={profile?.nip05} pubkey={peerPubkey} size="xs" />
            </div>
            <p className="text-[10px] text-amber-500 flex items-center gap-1 font-medium">
              <Shield className="w-2.5 h-2.5" /> Message request
            </p>
          </div>
        </Link>
      </div>

      {/* Gated content */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="text-center max-w-xs space-y-4">
          <div className="w-16 h-16 rounded-2xl bg-amber-500/10 flex items-center justify-center mx-auto">
            <Shield className="w-8 h-8 text-amber-500" />
          </div>
          <div>
            <h3 className="text-sm font-bold mb-1">Message Request</h3>
            <p className="text-xs text-muted-foreground leading-relaxed">
              <strong>{name}</strong> wants to message you. You don't follow this person.
              Accept to see their messages, or decline to hide this request.
            </p>
          </div>

          {/* Blurred preview placeholder */}
          <div className="rounded-xl bg-muted/50 border border-border p-4 relative overflow-hidden">
            <div className="space-y-2 blur-md select-none pointer-events-none" aria-hidden="true">
              <div className="h-3 w-3/4 bg-muted-foreground/20 rounded" />
              <div className="h-3 w-1/2 bg-muted-foreground/20 rounded" />
              <div className="h-3 w-2/3 bg-muted-foreground/20 rounded" />
            </div>
            <div className="absolute inset-0 flex items-center justify-center">
              <Lock className="w-5 h-5 text-muted-foreground/30" />
            </div>
          </div>

          <div className="flex gap-2 justify-center pt-2">
            <button
              onClick={onDecline}
              className="px-5 py-2.5 rounded-xl border border-border text-sm font-medium hover:bg-muted transition-colors"
            >
              Decline
            </button>
            <button
              onClick={onAccept}
              className="px-5 py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors flex items-center gap-1.5"
            >
              <Check className="w-4 h-4" /> Accept
            </button>
          </div>

          <p className="text-[10px] text-muted-foreground">
            Accepting lets you read and reply to their messages.
          </p>
        </div>
      </div>
    </>
  );
}

interface DecryptedMessage {
  id: string;
  pubkey: string;
  content: string;
  created_at: number;
  decrypted: boolean;
}

// ── Helpers: detect media URLs in decrypted message content ─────────
const IMAGE_URL_RE = /https?:\/\/\S+\.(?:jpg|jpeg|png|gif|webp|svg|bmp|avif)(?:\?\S*)?/gi;
const VIDEO_URL_RE = /https?:\/\/\S+\.(?:mp4|webm|mov|mkv|avi)(?:\?\S*)?/gi;

/** Extract media URLs and remaining text from a message */
function parseMessageMedia(content: string): { text: string; images: string[]; videos: string[] } {
  const images = content.match(IMAGE_URL_RE) || [];
  const videos = content.match(VIDEO_URL_RE) || [];
  // Remove media URLs from text display
  let text = content;
  for (const url of [...images, ...videos]) {
    text = text.replace(url, '').trim();
  }
  return { text, images, videos };
}

function ChatArea({ peerPubkey, myPubkey, onBack, dmEvents }: {
  peerPubkey: string; myPubkey: string; onBack: () => void; dmEvents: any[];
}) {
  const { profile } = useNostrProfile(peerPubkey);
  const peerName = useDisplayName(peerPubkey, profile, peerPubkey.slice(0, 12));
  const { publish } = useNostrPublish();
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [decryptedMessages, setDecryptedMessages] = useState<DecryptedMessage[]>([]);
  const [decrypting, setDecrypting] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const decryptedIds = useRef(new Set<string>());
  const [deletedIds, setDeletedIds] = useState<Set<string>>(() => getDeletedDMs());
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  // ── Delete a DM ────────────────────────────────────────────────
  const handleDelete = useCallback(async (eventId: string) => {
    setDeleting(true);
    try {
      // Publish NIP-09 deletion request (kind 5)
      await publish({
        kind: 5,
        content: 'Deleted',
        tags: [['e', eventId]],
        created_at: Math.floor(Date.now() / 1000),
      });
    } catch (e) {
      console.warn('Failed to publish deletion event:', e);
      // Still hide locally even if relay publish fails
    }
    // Always hide locally
    markDMDeleted(eventId);
    setDeletedIds(prev => {
      const next = new Set(prev);
      next.add(eventId);
      return next;
    });
    setConfirmDeleteId(null);
    setDeleting(false);
  }, [publish]);

  // ── Media upload state ───────────────────────────────────────────
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [pendingMedia, setPendingMedia] = useState<{ url: string; type: 'image' | 'video' | 'audio' }[]>([]);
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);

  // Filter DMs for this conversation (excluding locally deleted)
  const rawMessages = dmEvents
    .filter(ev => {
      if (deletedIds.has(ev.id)) return false;
      const isFromMe = ev.pubkey === myPubkey && ev.tags.some((t: string[]) => t[0] === 'p' && t[1] === peerPubkey);
      const isToMe = ev.pubkey === peerPubkey && ev.tags.some((t: string[]) => t[0] === 'p' && t[1] === myPubkey);
      return isFromMe || isToMe;
    })
    .sort((a, b) => a.created_at - b.created_at);

  // Decrypt messages
  useEffect(() => {
    if (rawMessages.length === 0) return;

    const decryptAll = async () => {
      setDecrypting(true);
      const signer = (window as any).nostr;
      const results: DecryptedMessage[] = [];

      for (const msg of rawMessages) {
        // Skip if already decrypted
        if (decryptedIds.current.has(msg.id)) {
          const existing = decryptedMessages.find(d => d.id === msg.id);
          if (existing) { results.push(existing); continue; }
        }

        let plaintext = msg.content;
        let decrypted = false;

        try {
          if (signer?.nip04?.decrypt) {
            // Decrypt: the "sender" for decrypt is whoever is NOT us
            const otherPubkey = msg.pubkey === myPubkey ? peerPubkey : msg.pubkey;
            plaintext = await signer.nip04.decrypt(otherPubkey, msg.content);
            decrypted = true;
          }
        } catch (e) {
          // If decryption fails, show a placeholder
          plaintext = '[Encrypted message — unable to decrypt]';
        }

        decryptedIds.current.add(msg.id);
        results.push({
          id: msg.id,
          pubkey: msg.pubkey,
          content: plaintext,
          created_at: msg.created_at,
          decrypted,
        });
      }

      setDecryptedMessages(results);
      setDecrypting(false);
    };

    decryptAll();
  }, [rawMessages.length, peerPubkey, myPubkey]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [decryptedMessages.length]);

  // ── File upload handler ──────────────────────────────────────────
  const handleFileUpload = useCallback(async (file: File) => {
    setUploadError(null);
    setUploading(true);
    setUploadProgress(0);
    try {
      const result = await uploadMedia(file, setUploadProgress);
      setPendingMedia(prev => [...prev, result]);
    } catch (err) {
      console.error('DM media upload failed:', err);
      setUploadError(
        err instanceof UploadSizeError
          ? err.message
          : err instanceof Error ? err.message : 'Upload failed'
      );
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  }, []);

  // ── Clipboard paste for images ──────────────────────────────────
  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    const imageItem = Array.from(items).find(
      item => item.kind === 'file' && (item.type.startsWith('image/') || item.type.startsWith('video/'))
    );
    if (!imageItem) return;
    e.preventDefault();
    const file = imageItem.getAsFile();
    if (file) handleFileUpload(file);
  }, [handleFileUpload]);

  const removePendingMedia = (index: number) => {
    setPendingMedia(prev => prev.filter((_, i) => i !== index));
  };

  const handleSend = async () => {
    const hasText = message.trim().length > 0;
    const hasMedia = pendingMedia.length > 0;
    if (!hasText && !hasMedia) return;

    // Snapshot the content and clear the input immediately so the
    // user doesn't have to wait for relay round-trips to see it go.
    const textToSend = message.trim();
    const mediaToSend = [...pendingMedia];
    setMessage('');
    setPendingMedia([]);
    setUploadError(null);

    setSending(true);
    try {
      // Build message: text + media URLs on separate lines
      const parts: string[] = [];
      if (textToSend) parts.push(textToSend);
      for (const media of mediaToSend) {
        parts.push(media.url);
      }
      const fullMessage = parts.join('\n');

      // NIP-04: encrypt message (handled by extension)
      let encryptedContent = fullMessage;
      try {
        if ((window as any).nostr?.nip04) {
          encryptedContent = await (window as any).nostr.nip04.encrypt(peerPubkey, fullMessage);
        }
      } catch (e) {
        console.warn('NIP-04 encryption not available, sending plain');
      }

      await publish({
        kind: 4,
        content: encryptedContent,
        tags: [['p', peerPubkey]],
        created_at: Math.floor(Date.now() / 1000),
      });
    } catch (e) {
      console.error('Failed to send DM:', e);
      // Restore the message on failure so the user doesn't lose it
      setMessage(textToSend);
      setPendingMedia(mediaToSend);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {/* Chat Header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-border">
        <button onClick={onBack} className="md:hidden p-1 rounded-lg hover:bg-muted">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <Link to={`/profile/${peerPubkey}`} className="flex items-center gap-2">
          {profile?.picture ? (
            <img src={profile.picture} alt="" className="w-8 h-8 rounded-full object-cover" />
          ) : (
            <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
              <User className="w-4 h-4 text-primary/60" />
            </div>
          )}
          <div>
            <div className="flex items-center gap-1">
              <p className="font-semibold text-sm">{peerName}</p>
              <VerifiedBadge nip05={profile?.nip05} pubkey={peerPubkey} size="xs" />
            </div>
            <p className="text-[10px] text-muted-foreground flex items-center gap-1">
              <Lock className="w-2.5 h-2.5" /> Encrypted
            </p>
          </div>
        </Link>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {decrypting && decryptedMessages.length === 0 ? (
          <div className="text-center py-8">
            <div className="w-6 h-6 border-2 border-primary/20 border-t-primary rounded-full animate-spin mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">Decrypting messages...</p>
          </div>
        ) : decryptedMessages.length === 0 && rawMessages.length === 0 ? (
          <div className="text-center py-8">
            <Lock className="w-8 h-8 mx-auto text-muted-foreground/30 mb-2" />
            <p className="text-sm text-muted-foreground">Messages are encrypted end-to-end.</p>
            <p className="text-xs text-muted-foreground mt-1">Send a message to start the conversation.</p>
          </div>
        ) : (
          decryptedMessages.filter(m => !deletedIds.has(m.id)).map((msg, i) => {
            const { text, images, videos } = parseMessageMedia(msg.content);
            const isMine = msg.pubkey === myPubkey;
            const isConfirming = confirmDeleteId === msg.id;

            return (
              <div
                key={msg.id || i}
                className={`flex ${isMine ? 'justify-end' : 'justify-start'} group`}
              >
                {/* Delete button — left of own messages */}
                {isMine && !isConfirming && (
                  <button
                    onClick={() => setConfirmDeleteId(msg.id)}
                    className="self-center mr-1.5 p-1.5 rounded-full opacity-0 group-hover:opacity-100 hover:bg-red-500/10 text-muted-foreground/40 hover:text-red-500 transition-all shrink-0"
                    title="Delete message"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}

                <div className={`flex flex-col ${isMine ? 'items-end' : 'items-start'} max-w-[80%] min-w-0`}>
                  {/* Delete confirmation */}
                  {isConfirming && (
                    <div className="flex items-center gap-1.5 mb-1.5 px-3 py-2 rounded-xl bg-red-500/10 border border-red-500/20 animate-in fade-in">
                      <AlertTriangle className="w-3.5 h-3.5 text-red-500 shrink-0" />
                      <span className="text-[11px] text-red-600 dark:text-red-400 font-medium">Delete this message?</span>
                      <button
                        onClick={() => handleDelete(msg.id)}
                        disabled={deleting}
                        className="px-2 py-0.5 rounded-md bg-red-500 text-white text-[11px] font-medium hover:bg-red-600 disabled:opacity-50 shrink-0"
                      >
                        {deleting ? '...' : 'Delete'}
                      </button>
                      <button
                        onClick={() => setConfirmDeleteId(null)}
                        className="px-2 py-0.5 rounded-md border border-border text-[11px] text-muted-foreground hover:text-foreground shrink-0"
                      >
                        Cancel
                      </button>
                    </div>
                  )}

                  <div
                    className={`rounded-2xl text-sm ${
                      isMine
                        ? 'bg-primary text-primary-foreground rounded-br-md'
                        : 'bg-muted rounded-bl-md'
                    }`}
                  >
                    {/* Inline images */}
                    {images.length > 0 && (
                      <div className={`overflow-hidden ${text ? 'rounded-t-2xl' : isMine ? 'rounded-2xl rounded-br-md' : 'rounded-2xl rounded-bl-md'} ${images.length > 1 ? 'grid grid-cols-2 gap-0.5' : ''}`}>
                        {images.map((url, j) => (
                          <button
                            key={j}
                            onClick={() => setLightboxUrl(url)}
                            className="block w-full focus:outline-none"
                          >
                            <img
                              src={url}
                              alt=""
                              className={`w-full object-cover ${images.length > 1 ? 'max-h-40' : 'max-h-72'}`}
                              loading="lazy"
                            />
                          </button>
                        ))}
                      </div>
                    )}

                    {/* Inline videos */}
                    {videos.length > 0 && (
                      <div className={`overflow-hidden ${text ? 'rounded-t-2xl' : isMine ? 'rounded-2xl rounded-br-md' : 'rounded-2xl rounded-bl-md'}`}>
                        {videos.map((url, j) => (
                          <video
                            key={`v-${j}`}
                            src={url}
                            controls
                            preload="metadata"
                            className="w-full max-h-72"
                          />
                        ))}
                      </div>
                    )}

                    {/* Text content + timestamp */}
                    <div className="px-3 py-2">
                      {text && <p className="whitespace-pre-wrap break-words">{text}</p>}
                      <p className={`text-[10px] ${text ? 'mt-0.5' : ''} ${
                        isMine ? 'text-primary-foreground/60' : 'text-muted-foreground'
                      }`}>
                        {timeAgo(msg.created_at)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Media preview strip */}
      {(pendingMedia.length > 0 || uploadError) && (
        <div className="border-t border-border px-3 pt-2 pb-1">
          {uploadError && (
            <div className="flex items-center gap-2 mb-2 px-2 py-1.5 rounded-lg bg-red-500/10 border border-red-500/20">
              <p className="text-xs text-red-500 flex-1">{uploadError}</p>
              <button onClick={() => setUploadError(null)} className="text-red-500 hover:text-red-400">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
          {pendingMedia.length > 0 && (
            <div className="flex gap-2 overflow-x-auto pb-1">
              {pendingMedia.map((media, i) => (
                <div key={i} className="relative shrink-0 group">
                  {media.type === 'image' ? (
                    <img src={media.url} alt="" className="w-16 h-16 rounded-lg object-cover border border-border" />
                  ) : media.type === 'video' ? (
                    <div className="w-16 h-16 rounded-lg border border-border bg-muted flex items-center justify-center">
                      <Play className="w-6 h-6 text-muted-foreground" />
                    </div>
                  ) : (
                    <div className="w-16 h-16 rounded-lg border border-border bg-muted flex items-center justify-center">
                      <Film className="w-6 h-6 text-muted-foreground" />
                    </div>
                  )}
                  <button
                    onClick={() => removePendingMedia(i)}
                    className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 sm:opacity-100 transition-opacity shadow"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Upload progress bar */}
      {uploading && (
        <div className="px-3">
          <div className="h-1 bg-muted rounded-full overflow-hidden">
            <div className="h-full bg-primary rounded-full transition-all duration-300" style={{ width: `${uploadProgress}%` }} />
          </div>
        </div>
      )}

      {/* Input */}
      <div className="border-t border-border p-3">
        <div className="flex items-end gap-2">
          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,image/heic,image/heif,video/mp4,video/webm,video/quicktime,video/*"
            className="hidden"
            onChange={async (e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              e.target.value = '';
              handleFileUpload(file);
            }}
          />

          {/* Media upload button */}
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className={`p-2.5 rounded-full transition-colors shrink-0 mb-0.5 ${
              uploading
                ? 'text-primary'
                : 'text-muted-foreground hover:text-primary hover:bg-primary/5'
            }`}
            title="Send image or video"
          >
            {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Camera className="w-5 h-5" />}
          </button>

          <textarea
            ref={textareaRef}
            value={message}
            onChange={e => setMessage(e.target.value)}
            onPaste={handlePaste}
            placeholder="Type a message..."
            className="flex-1 bg-muted rounded-2xl px-4 py-2.5 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none min-h-[42px] max-h-[120px]"
            rows={1}
            onKeyDown={e => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                handleSend();
              }
            }}
            onInput={e => {
              const target = e.target as HTMLTextAreaElement;
              target.style.height = 'auto';
              target.style.height = Math.min(target.scrollHeight, 120) + 'px';
            }}
          />
          <button
            onClick={handleSend}
            disabled={(!message.trim() && pendingMedia.length === 0) || sending}
            className="p-2.5 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 transition-colors shrink-0 mb-0.5"
          >
            <Send className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Image lightbox */}
      {lightboxUrl && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setLightboxUrl(null)}
        >
          <button
            onClick={() => setLightboxUrl(null)}
            className="absolute top-4 right-4 p-2 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
          <img
            src={lightboxUrl}
            alt=""
            className="max-w-full max-h-full object-contain rounded-lg"
            onClick={e => e.stopPropagation()}
          />
        </div>
      )}
    </>
  );
}

